import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { useDebug } from '../contexts/DebugContext';
import { useAppHeaderHeight } from '../contexts/LayoutContext';
import { PageHeader, Banner, IconButton, DebugPanel } from './ui';

// Default budget window length (months) when a budget hasn't been loaded yet.
const DEFAULT_MONTHS = 6;

// Split an integer total evenly across n months (remainder into month 1)
function splitEven(total, n) {
  const t = Math.round(Number(total) || 0);
  const count = Math.max(1, n | 0);
  const base = Math.trunc(t / count);
  const rem = t - base * count;
  return Array.from({ length: count }, (_, i) => (i === 0 ? base + rem : base));
}
const toInt = (v) => Math.round(Number(v) || 0);
// A line is "zero" when every budget month value, the window total, and the
// actual invoiced amount are all zero.
const isZeroLine = (l) => (l.amounts || []).every((a) => toInt(a) === 0)
  && toInt(l.quarterTotal) === 0 && (Number(l.actualInvoiced) || 0) === 0;
// Money display: "$1,000" (rounded). Editable boxes reuse this and parse with parseNum.
// Whole dollars ($1,235), shared with Projections Admin.
export const fmtMoney = (n) => (n === '' || n == null || isNaN(Number(n))) ? '' : `$${Math.round(Number(n)).toLocaleString('en-US')}`;
// Parse a possibly-formatted money/number string back to an integer
const parseNum = (s) => { const v = parseInt(String(s).replace(/[^0-9-]/g, ''), 10); return isNaN(v) ? 0 : v; };

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// Real month/year labels for the n months of the window (period = YYYY-MM-01)
function monthHeaders(period, n) {
  const count = Math.max(1, n || DEFAULT_MONTHS);
  const m = /^(\d{4})-(\d{2})-/.exec(period || '');
  if (!m) return Array.from({ length: count }, (_, i) => `Month ${i + 1}`);
  const year = parseInt(m[1], 10);
  const startMonth = parseInt(m[2], 10) - 1; // 0-based
  return Array.from({ length: count }, (_, i) => {
    const idx = startMonth + i;
    return `${MONTH_NAMES[idx % 12]} ${year + Math.floor(idx / 12)}`;
  });
}

// First-of-quarter date string for the current quarter (YYYY-MM-01)
function currentQuarterPeriod() {
  const now = new Date();
  const q = Math.floor(now.getMonth() / 3);
  return `${now.getFullYear()}-${String(q * 3 + 1).padStart(2, '0')}-01`;
}

// Map a server line into the editable local shape
function toLocal(line) {
  const m = line.months || [];
  return {
    lineGroupId: line.lineGroupId,
    budgetLineType: line.budgetLineType,
    displayedBy: line.displayedBy || 'month',
    locked: !!line.locked,
    amounts: m.map((x) => toInt(x.amount)),
    quarterTotal: toInt(line.quarterTotal),
    probabilityPct: line.probability != null ? Math.round(Number(line.probability)) : '',
    estimatedValue: line.estimatedValue != null ? line.estimatedValue : '',
    description: line.description || '',
    // reference (read-only) fields
    jobNo: line.jobNo, jobTaskNo: line.jobTaskNo, jobName: line.jobName, taskName: line.taskName,
    customerName: line.customerName, remainingBudget: line.remainingBudget,
    last3: line.invoiceTotalLast3 != null ? Number(line.invoiceTotalLast3) : null,
    actualInvoiced: line.actualInvoiced != null ? Number(line.actualInvoiced) : null,
    opportunityNo: line.opportunityNo, opportunityName: line.opportunityName,
    actualJobInfo: line.actualJobInfo || '',
  };
}

function calcValue(line) {
  const ev = Number(line.estimatedValue) || 0;
  const p = (Number(line.probabilityPct) || 0) / 100;
  return Math.round(ev * p);
}

export default function BudgetView() {
  const { debugMode } = useDebug();
  // Column headers stick below the sticky app header.
  const NAVBAR_TOP = useAppHeaderHeight();
  // Measured height of the sticky Goal footer, so the aligned totals row can
  // sit directly on top of it.
  const goalRef = useRef(null);
  const [goalH, setGoalH] = useState(150);
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [status, setStatus] = useState('none'); // none | open | submitted
  const [canCreate, setCanCreate] = useState(false);
  const [lines, setLines] = useState([]);
  const [monthCount, setMonthCount] = useState(DEFAULT_MONTHS);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [actualModified, setActualModified] = useState(null);
  const [hideZero, setHideZero] = useState(false);
  const [navDebug, setNavDebug] = useState([]);
  const [adminLocked, setAdminLocked] = useState(false);
  // Universal admin setting: hide Opportunities/Other sections and the Goal panel.
  const [hideNonJobsAndGoal, setHideNonJobsAndGoal] = useState(true);
  const [collapsed, setCollapsed] = useState({});
  const toggleSection = (name) => setCollapsed((c) => ({ ...c, [name]: !c[name] }));
  const [ttmCost, setTtmCost] = useState(null);
  const [margin, setMargin] = useState(0.20);
  const [marginPct, setMarginPct] = useState('20'); // editable % string

  const applyBudget = (data) => {
    setStatus(data.exists ? data.status : 'none');
    setCanCreate(!!data.canCreate);
    const mapped = (data.lines || []).map(toLocal);
    setLines(mapped);
    const n = mapped.reduce((mx, l) => Math.max(mx, l.amounts.length), 0);
    if (n > 0) setMonthCount(n);
    setActualModified(data.actualInvoiceModified || null);
    if (data.adminLocked !== undefined) setAdminLocked(!!data.adminLocked);
    if (data.ttmCost !== undefined) setTtmCost(data.ttmCost);
    if (data.margin !== undefined && data.margin != null) {
      setMargin(Number(data.margin));
      setMarginPct(String(Math.round(Number(data.margin) * 100)));
    }
    // Surface any NAV calls made by this request (e.g. the TTM cost fetch) in
    // the debug panel.
    const calls = [];
    [data._navPush, data._debug].forEach((arr) => { if (Array.isArray(arr)) arr.forEach((x) => { if (x && x.request) calls.push(x); }); });
    setNavDebug(calls);
  };

  // Live margin from the input (so Revenue Required recalculates as you type),
  // falling back to the saved margin when the input isn't a valid percentage.
  const liveMargin = (() => { const p = parseFloat(marginPct); return (!isNaN(p) && p >= 0 && p < 100) ? p / 100 : margin; })();
  // Revenue Required = TTM cost / (1 - margin), whole numbers.
  const revenueRequired = (ttmCost != null && liveMargin < 1) ? Math.round(ttmCost / (1 - liveMargin)) : null;

  const commitMargin = async (pctStr) => {
    const pct = parseFloat(pctStr);
    if (isNaN(pct) || pct < 0 || pct >= 100) { setMarginPct(String(Math.round(margin * 100))); return; }
    const m = Math.round(pct) / 100;
    setMargin(m);
    setMarginPct(String(Math.round(pct)));
    try { await api.setBudgetMargin(selectedPeriod, m); }
    catch (e) { setError(e.message || 'Failed to save margin'); }
  };

  // Keep the aligned totals row sitting exactly on top of the Goal footer
  // (re-measured after every render; setGoalH is guarded by the size check).
  useLayoutEffect(() => {
    const h = goalRef.current ? goalRef.current.offsetHeight : 0;
    if (h && Math.abs(h - goalH) > 1) setGoalH(h);
  });
  // When the Goal panel is hidden, the sticky headers sit directly under the
  // app chrome (no goal footer to offset by).
  const effectiveGoalH = hideNonJobsAndGoal ? 0 : goalH;

  // Goal summary shown at the bottom of the budget. On the open budget the
  // margin is editable; on the submitted budget it's read-only and extra
  // variances (vs actual invoiced) are shown. Hidden entirely when the
  // "Hide Non-Jobs and Goal" admin setting is on.
  const renderGoalPanel = (submitted) => {
    if (hideNonJobsAndGoal) return null;
    const budgetVsReq = revenueRequired != null ? totals.q - revenueRequired : null;
    const actualVsReq = revenueRequired != null ? totals.actual - revenueRequired : null;
    const item = (label, value, negativeRed) => (
      <div style={{ minWidth: '150px' }}>
        <div style={{ fontSize: '11px', color: 'var(--gray-500)' }}>{label}</div>
        <div style={{ fontSize: '15px', fontWeight: 700, color: negativeRed && Number(value) < 0 ? '#dc2626' : undefined }}>
          {value == null ? '—' : fmtMoney(value)}
        </div>
      </div>
    );
    return (
      <div ref={goalRef} className="card" style={{ ...goalStickyTop, marginBottom: '12px', padding: '10px 16px' }}>
        <div style={{ fontWeight: 700, fontSize: '13px', marginBottom: '10px' }}>Quarter Goal</div>
        <div style={{ display: 'flex', gap: '28px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          {item('Avg Quarterly TTM Cost', ttmCost)}
          <div style={{ minWidth: '110px' }}>
            <div style={{ fontSize: '11px', color: 'var(--gray-500)' }}>Margin</div>
            {submitted ? (
              <div style={{ fontSize: '15px', fontWeight: 700 }}>{Math.round(margin * 100)}%</div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <input
                  type="number" min="0" max="99" value={marginPct}
                  onChange={(e) => setMarginPct(e.target.value)}
                  onBlur={(e) => commitMargin(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                  style={{ width: '60px', fontSize: '15px', fontWeight: 700, padding: '2px 6px', border: '1px solid var(--gray-300)', borderRadius: '4px' }}
                />
                <span style={{ fontSize: '15px', fontWeight: 700 }}>%</span>
              </div>
            )}
          </div>
          {item('Revenue Required', revenueRequired)}
          {item('Total Projected', totals.q)}
          {item('Projected − Required', budgetVsReq, true)}
          {submitted && item('Actual − Required', actualVsReq, true)}
        </div>
      </div>
    );
  };

  const loadBudget = useCallback(async (period) => {
    if (!period) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.getBudget(period, debugMode);
      applyBudget(data);
    } catch (e) {
      setError(e.message || 'Failed to load projections');
    }
    setLoading(false);
  }, [debugMode]);

  useEffect(() => {
    (async () => {
      try {
        const resp = await api.getBudgetPeriods(debugMode);
        const p = resp.periods;
        setPeriods(p || []);
        if (resp.hideNonJobsAndGoal !== undefined) setHideNonJobsAndGoal(!!resp.hideNonJobsAndGoal);
        // Default to the current quarter when present (periods run oldest→newest;
        // in debug mode the list also includes the prior 2 years). Fall back to
        // the newest period, then the oldest.
        const cur = currentQuarterPeriod();
        const initial = (p && p.find((x) => x.period === cur)) ? cur
          : (p && p.length ? p[p.length - 1].period : '');
        setSelectedPeriod(initial);
        if (initial) await loadBudget(initial);
        else setLoading(false);
      } catch (e) {
        setError(e.message || 'Failed to load projection quarters');
        setLoading(false);
      }
    })();
    // Load once on mount (debugMode only changes what later calls report).
  }, [loadBudget]);

  // Move to the previous (-1) or next (+1) quarter in the loaded periods list.
  const goQuarter = (delta) => {
    const idx = periods.findIndex((p) => p.period === selectedPeriod);
    const next = periods[idx + delta];
    if (next) {
      setSelectedPeriod(next.period);
      loadBudget(next.period);
    }
  };

  const readOnly = status === 'submitted';

  // ---- Line editing ----
  const updateLine = (idx, patch) => {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };

  const commitLine = useCallback(async (line) => {
    if (readOnly) return;
    const n = line.amounts ? line.amounts.length : monthCount;
    let months;
    if (line.displayedBy === 'quarter') months = splitEven(line.quarterTotal, n);
    else months = (line.amounts || []).map(toInt);
    const payload = {
      lineGroupId: line.lineGroupId,
      displayedBy: line.displayedBy,
      months,
      probability: line.probabilityPct === '' || line.probabilityPct == null ? null : Number(line.probabilityPct),
      estimatedValue: line.estimatedValue === '' || line.estimatedValue == null ? null : Number(line.estimatedValue),
      description: line.description,
    };
    try {
      await api.saveBudget(selectedPeriod, [payload]);
    } catch (e) {
      setError(e.message || 'Failed to save');
    }
  }, [selectedPeriod, readOnly, monthCount]);

  const onMonthChange = (idx, mi, value) => {
    const v = toInt(value);
    setLines((prev) => prev.map((l, i) => {
      if (i !== idx) return l;
      const amounts = l.amounts.slice();
      amounts[mi] = v;
      return { ...l, amounts, quarterTotal: amounts.reduce((s, a) => s + toInt(a), 0) };
    }));
  };

  const onQuarterChange = (idx, value) => {
    updateLine(idx, { quarterTotal: toInt(value) });
  };

  const setToggle = (idx, mode) => {
    setLines((prev) => prev.map((l, i) => {
      if (i !== idx) return l;
      let next;
      if (mode === 'quarter') {
        // keep the current window total (sum) as the editable value
        next = { ...l, displayedBy: 'quarter' };
      } else {
        // divide the window total across the months
        const amounts = splitEven(l.quarterTotal, l.amounts.length || monthCount);
        next = { ...l, displayedBy: 'month', amounts, quarterTotal: amounts.reduce((s, a) => s + a, 0) };
      }
      // persist after state settles
      setTimeout(() => commitLine({ ...l, ...next }), 0);
      return next;
    }));
  };

  const pullOver = (idx) => {
    setLines((prev) => prev.map((l, i) => {
      if (i !== idx) return l;
      const total = l.budgetLineType === 'job' ? toInt(l.remainingBudget) : calcValue(l);
      const next = { ...l, displayedBy: 'quarter', quarterTotal: total };
      setTimeout(() => commitLine(next), 0);
      return next;
    }));
  };

  // Jobs: copy DOUBLE the Last 3 Months invoice total into the window total
  // (the window is 6 months, so twice the last-3-months figure).
  const pullLast3 = (idx) => {
    setLines((prev) => prev.map((l, i) => {
      if (i !== idx) return l;
      const next = { ...l, displayedBy: 'quarter', quarterTotal: toInt(l.last3) * 2 };
      setTimeout(() => commitLine(next), 0);
      return next;
    }));
  };

  // Populate job lines that currently have a $0 budget with DOUBLE their Last 3
  // Months invoicing (leaves jobs that already have a budget untouched).
  const doPopulateAllLast3 = () => {
    if (readOnly) return;
    setLines((prev) => prev.map((l) => {
      if (l.budgetLineType !== 'job' || toInt(l.quarterTotal) !== 0) return l;
      const next = { ...l, displayedBy: 'quarter', quarterTotal: toInt(l.last3) * 2 };
      setTimeout(() => commitLine(next), 0);
      return next;
    }));
  };

  // Collect NAV request/response pairs from a debug-mode API response.
  const collectNavCalls = (data) => {
    const calls = [];
    const add = (arr) => { if (Array.isArray(arr)) arr.forEach((x) => { if (x && x.request) calls.push(x); }); };
    add(data && data._navPush);
    add(data && data._debug);
    return calls;
  };

  // ---- Budget-level actions ----
  const doCreate = async () => {
    setBusy(true); setError(null);
    try { applyBudget(await api.createBudget(selectedPeriod, debugMode)); }
    catch (e) { setError(e.message || 'Failed to create projections'); }
    setBusy(false);
  };
  const doUnsubmit = async () => {
    if (!window.confirm('Unsubmit these projections? They will be unlocked so you can edit them again.')) return;
    setBusy(true); setError(null); setNavDebug([]);
    try { const data = await api.unsubmitBudget(selectedPeriod, debugMode); applyBudget(data); setNavDebug(collectNavCalls(data)); }
    catch (e) { setError(e.message || 'Failed to unsubmit projections'); }
    setBusy(false);
  };
  const doLoadActuals = async () => {
    setBusy(true); setError(null); setNavDebug([]);
    try { const data = await api.loadBudgetActuals(selectedPeriod, debugMode); applyBudget(data); setNavDebug(collectNavCalls(data)); }
    catch (e) { setError(e.message || 'Failed to load actual invoiced amounts'); }
    setBusy(false);
  };
  const doNavResync = async () => {
    setBusy(true); setError(null);
    try {
      await api.refreshSync();
      applyBudget(await api.reconcileBudget(selectedPeriod, debugMode));
    } catch (e) { setError(e.message || 'Failed to resync from Navision'); }
    setBusy(false);
  };
  const doAddOther = async () => {
    setBusy(true); setError(null);
    try { applyBudget(await api.addBudgetOtherLine(selectedPeriod)); }
    catch (e) { setError(e.message || 'Failed to add line'); }
    setBusy(false);
  };
  const doDeleteLine = async (lineGroupId) => {
    if (!window.confirm('Delete this line?')) return;
    setBusy(true); setError(null);
    try { applyBudget(await api.deleteBudgetLine(selectedPeriod, lineGroupId)); }
    catch (e) { setError(e.message || 'Failed to delete line'); }
    setBusy(false);
  };
  const doSubmit = async () => {
    if (!window.confirm('Submit these projections? They will be locked and you will not be able to create new projections for this quarter.')) return;
    setBusy(true); setError(null); setNavDebug([]);
    try { const data = await api.submitBudget(selectedPeriod, debugMode); applyBudget(data); setNavDebug(collectNavCalls(data)); }
    catch (e) { setError(e.message || 'Failed to submit projections'); }
    setBusy(false);
  };
  const doExport = () => {
    const url = api.getBudgetExportUrl(selectedPeriod);
    window.open(url, '_blank');
  };

  const monthLabels = monthHeaders(selectedPeriod, monthCount);
  const N = monthLabels.length;

  const jobLines = lines.map((l, i) => ({ l, i })).filter((x) => x.l.budgetLineType === 'job')
    // Group by job, then job task, so tasks of the same job sit together.
    .sort((a, b) => (a.l.jobNo || '').localeCompare(b.l.jobNo || '')
      || (a.l.jobTaskNo || '').localeCompare(b.l.jobTaskNo || ''));
  const oppLines = lines.map((l, i) => ({ l, i })).filter((x) => x.l.budgetLineType === 'opportunity');
  const otherLines = lines.map((l, i) => ({ l, i })).filter((x) => x.l.budgetLineType === 'other');

  // Grand totals per month + window. Per-window lines contribute 1/N of the
  // window total to each month.
  const totals = (() => {
    const m = Array.from({ length: N }, () => 0);
    let q = 0;
    for (const l of lines) {
      const qt = toInt(l.quarterTotal);
      if (l.displayedBy === 'quarter') {
        const per = qt / N;
        for (let k = 0; k < N; k++) m[k] += per;
      } else {
        for (let k = 0; k < N; k++) m[k] += toInt(l.amounts[k]);
      }
      q += qt;
    }
    const actual = lines.reduce((s, l) => s + (Number(l.actualInvoiced) || 0), 0);
    const qTotal = Math.round(q);
    return { m: m.map(Math.round), q: qTotal, actual: Math.round(actual), variance: Math.round(actual) - qTotal };
  })();

  const numStyle = { width: '92px', textAlign: 'right', fontSize: '12px', padding: '2px 4px' };
  const cellStyle = { padding: '6px 8px', fontSize: '12px', borderBottom: '1px solid var(--gray-100)' };
  const headStyle = { padding: '6px 8px', fontSize: '11px', fontWeight: 600, textAlign: 'left', borderBottom: '2px solid var(--gray-300)', color: 'var(--gray-600)' };

  // Amount cells (months + window total + toggle) shared across sections
  const renderAmountCells = (l, i) => {
    const perQuarter = l.displayedBy === 'quarter';
    return (
      <>
        {monthLabels.map((_, mi) => (
          <td key={mi} style={{ ...cellStyle, textAlign: 'right' }}>
            {perQuarter ? (
              <span style={{ color: 'var(--gray-300)' }}>—</span>
            ) : (
              <input
                type="text"
                inputMode="numeric"
                disabled={readOnly}
                value={fmtMoney(l.amounts[mi])}
                onChange={(e) => onMonthChange(i, mi, parseNum(e.target.value))}
                onBlur={() => commitLine(lines[i])}
                style={numStyle}
              />
            )}
          </td>
        ))}
        <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 600 }}>
          {perQuarter ? (
            <input
              type="text"
              inputMode="numeric"
              disabled={readOnly}
              value={fmtMoney(l.quarterTotal)}
              onChange={(e) => onQuarterChange(i, parseNum(e.target.value))}
              onBlur={() => commitLine(lines[i])}
              style={numStyle}
            />
          ) : (
            <span>{fmtMoney(l.quarterTotal)}</span>
          )}
        </td>
        <td style={{ ...cellStyle, textAlign: 'center' }}>
          <button
            className="btn btn-sm btn-outline"
            disabled={readOnly}
            onClick={() => setToggle(i, perQuarter ? 'month' : 'quarter')}
            title={perQuarter ? 'Switch to per-month entry' : 'Switch to a single 6-month total'}
            style={{ fontSize: '10px', padding: '2px 6px' }}
          >{perQuarter ? '6-mo Total' : 'Per Month'}</button>
        </td>
      </>
    );
  };

  const renderPullOver = (l, i) => (
    <td style={{ ...cellStyle, textAlign: 'center' }}>
      <button
        className="btn btn-sm btn-outline"
        disabled={readOnly}
        onClick={() => pullOver(i)}
        title={l.budgetLineType === 'job' ? 'Move all remaining budget to the total.' : 'Move Calculated Value to the total.'}
        style={{ fontSize: '11px', padding: '2px 8px', whiteSpace: 'nowrap' }}
      >&#8677; Move All</button>
      {l.budgetLineType === 'job' && l.last3 != null && l.remainingBudget != null && toInt(l.last3) <= toInt(l.remainingBudget) && (
        <div style={{ marginTop: '4px' }}>
          <button
            className="btn btn-sm btn-outline"
            disabled={readOnly}
            onClick={() => pullLast3(i)}
            title="Copy double the last 3 months invoicing to the 6-month total."
            style={{ fontSize: '11px', padding: '2px 8px', whiteSpace: 'nowrap' }}
          >Last 3 mo. avg.</button>
        </div>
      )}
    </td>
  );

  const sectionRowStyle = {
    padding: '8px', fontSize: '13px', fontWeight: 700, color: 'var(--gray-700, #374151)',
    background: 'var(--gray-50, #f8fafc)', borderTop: '2px solid var(--gray-300)', borderBottom: '1px solid var(--gray-200)',
  };
  // Sticky header stack (below the tab-bar): Goal panel, then the aligned
  // Totals row, then the month/column labels. Offsets stack by measured
  // heights so they abut cleanly. Single page scroll (no internal scroll box).
  const LABEL_H = 30; // approx month-label row height (px)
  const goalStickyTop = { position: 'sticky', top: `${NAVBAR_TOP}px`, zIndex: 6, background: '#fff', boxShadow: '0 2px 6px rgba(0,0,0,0.06)' };
  const stickyHead = { ...headStyle, position: 'sticky', top: `${NAVBAR_TOP + effectiveGoalH}px`, zIndex: 5, background: '#cbd5e1', color: 'var(--gray-700, #374151)' };
  const stickyTotals = { ...cellStyle, position: 'sticky', top: `${NAVBAR_TOP + effectiveGoalH + LABEL_H}px`, zIndex: 4, background: '#cbd5e1', color: 'var(--gray-700, #374151)', fontWeight: 700, borderBottom: '2px solid var(--gray-300)' };
  const scrollWrap = {};
  const mutedDash = <span style={{ color: 'var(--gray-300)' }}>—</span>;

  // Column counts for colSpans (derived from the month count N).
  const OPEN_COLS = N + 8;       // name + est + last3/prob + calc + pullover + N + total + toggle + actions
  const OPEN_BAND_REST = N + 4;  // columns after the 4 label columns in a section band
  const SUB_COLS = N + 4;        // name + N + total + actual + variance

  // Read-only name/description cell content (used by the submitted view)
  const lineName = (l) => {
    if (l.budgetLineType === 'job') {
      return (
        <>
          <div style={{ fontWeight: 500 }}>{l.jobNo}/{l.jobTaskNo} — {l.jobName}</div>
          <div style={{ fontSize: '11px', color: 'var(--gray-500)' }}>{l.taskName}{l.customerName ? ` · ${l.customerName}` : ''}</div>
        </>
      );
    }
    if (l.budgetLineType === 'opportunity') {
      return (
        <>
          <div style={{ fontWeight: 500 }}>{l.opportunityName}</div>
          <div style={{ fontSize: '11px', color: 'var(--gray-500)' }}>
            {l.opportunityNo}{l.actualJobInfo ? ` / ${l.actualJobInfo}` : ''}
          </div>
        </>
      );
    }
    return <div>{l.description || mutedDash}</div>;
  };

  // Section band row (open view). Carries the per-section column labels for
  // columns 2/3/4, which differ by section (jobs vs opportunities/other).
  const sectionBand = (title, labels = ['', '', ''], addBtn = null) => (
    <tr>
      <td style={sectionRowStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSection(title)} title={collapsed[title] ? 'Expand section' : 'Collapse section'}>
            {collapsed[title] ? '▸' : '▾'} {title}
          </span>{addBtn}
        </div>
      </td>
      <td style={{ ...sectionRowStyle, textAlign: 'right', fontWeight: 600 }}>{labels[0]}</td>
      <td style={{ ...sectionRowStyle, textAlign: 'right', fontWeight: 600 }}>{labels[1]}</td>
      <td style={{ ...sectionRowStyle, textAlign: 'right', fontWeight: 600 }}>{labels[2]}</td>
      <td style={sectionRowStyle} colSpan={OPEN_BAND_REST}></td>
    </tr>
  );

  // One row shape shared by all three sections so common columns line up
  const renderBudgetRow = (l, i) => {
    const isJob = l.budgetLineType === 'job';
    const isOpp = l.budgetLineType === 'opportunity';
    const isOther = l.budgetLineType === 'other';
    return (
      <tr key={l.lineGroupId}>
        {/* Name / Description */}
        <td style={cellStyle}>
          {isJob && (
            <div style={{ paddingLeft: '16px' }}>
              <div>{l.jobTaskNo} {l.taskName}</div>
            </div>
          )}
          {isOpp && (
            <>
              <div style={{ fontWeight: 500 }}>{l.opportunityName}</div>
              <div style={{ fontSize: '11px', color: 'var(--gray-500)' }}>{l.opportunityNo}</div>
            </>
          )}
          {isOther && (
            <input
              type="text" className="form-control" disabled={readOnly}
              value={l.description}
              onChange={(e) => updateLine(i, { description: e.target.value })}
              onBlur={() => commitLine(lines[i])}
              placeholder="Description"
              style={{ fontSize: '12px', padding: '2px 6px', width: '100%', minWidth: '180px' }}
            />
          )}
        </td>
        {/* Estimated Value (jobs leave blank; their budget shows in Calculated Value) */}
        <td style={{ ...cellStyle, textAlign: 'right' }}>
          {isJob && mutedDash}
          {isOpp && (l.estimatedValue != null && l.estimatedValue !== '' ? fmtMoney(l.estimatedValue) : mutedDash)}
          {isOther && (
            <input
              type="text" inputMode="numeric" disabled={readOnly}
              value={fmtMoney(l.estimatedValue)}
              onChange={(e) => updateLine(i, { estimatedValue: e.target.value.trim() === '' ? '' : parseNum(e.target.value) })}
              onBlur={() => commitLine(lines[i])}
              style={numStyle}
            />
          )}
        </td>
        {/* Last 3 Months (jobs) / Probability (opportunities, other) */}
        <td style={{ ...cellStyle, textAlign: 'right' }}>
          {isJob ? (l.last3 != null ? fmtMoney(l.last3) : mutedDash) : (
            <>
              <input
                type="number" className="no-spinners" disabled={readOnly}
                value={l.probabilityPct}
                onChange={(e) => updateLine(i, { probabilityPct: e.target.value === '' ? '' : Number(e.target.value) })}
                onBlur={() => commitLine(lines[i])}
                style={{ ...numStyle, width: '56px' }}
              />%
            </>
          )}
        </td>
        {/* Calculated Value (jobs show their remaining budget here) */}
        <td style={{ ...cellStyle, textAlign: 'right' }}>
          {isJob ? (l.remainingBudget != null ? fmtMoney(l.remainingBudget) : mutedDash) : fmtMoney(calcValue(l))}
        </td>
        {renderPullOver(l, i)}
        {renderAmountCells(l, i)}
        {/* Actions */}
        <td style={{ ...cellStyle, textAlign: 'center' }}>
          {isOther && !readOnly && (
            <IconButton icon="delete" title="Delete line" onClick={() => doDeleteLine(l.lineGroupId)} />
          )}
        </td>
      </tr>
    );
  };

  if (loading) return <div className="loading"><div className="spinner" />Loading projections...</div>;

  const curIndex = periods.findIndex((p) => p.period === selectedPeriod);
  const curMeta = curIndex >= 0 ? periods[curIndex] : null;
  // Use the live budget status (reflects submit/unsubmit this session) rather
  // than the status captured in the periods list when it was first loaded.
  const curStatusLabel = status === 'submitted' ? ' (submitted)'
    : status === 'open' ? ' (open)' : '';

  return (
    <div className="wide-section" style={{ padding: '16px' }}>
      <PageHeader
        title="Projections"
        subtitle={`${curMeta ? curMeta.label : selectedPeriod}${curStatusLabel}`}
        onPrev={curIndex > 0 ? () => goQuarter(-1) : null}
        onNext={curIndex >= 0 && curIndex < periods.length - 1 ? () => goQuarter(1) : null}
        prevTitle="Previous quarter"
        nextTitle="Next quarter"
        actions={status !== 'none' && (
          <>
            {status === 'open' && (
              <button className="btn btn-sm btn-outline" disabled={busy} onClick={doNavResync}>
                {busy ? 'Working…' : 'Resync from Navision'}
              </button>
            )}
            {status === 'open' && (
              <button className="btn btn-sm btn-outline" disabled={busy} onClick={doPopulateAllLast3}
                title="Auto-populate jobs from double their last 3 months invoicing">
                Populate From 3 mo. Avg.
              </button>
            )}
            <button className="btn btn-sm btn-outline" onClick={doExport}>Download to Excel</button>
            {status === 'open' && (
              <button className="btn btn-sm btn-primary" disabled={busy} onClick={doSubmit}>Submit Projections</button>
            )}
            {status === 'submitted' && !adminLocked && (
              <button className="btn btn-sm btn-outline" disabled={busy} onClick={doUnsubmit}>Unsubmit Projections</button>
            )}
          </>
        )}
      />

      {status === 'submitted' && (
        <Banner kind="ok">
          These projections have been submitted and are locked.
          {adminLocked && ' This quarter has been locked in Projections Admin, so it can no longer be unsubmitted.'}
        </Banner>
      )}
      <Banner kind="err">{error}</Banner>

      <DebugPanel calls={navDebug} />

      {status === 'none' && (
        <div className="card">
          <div style={{ padding: '16px' }}>
            {canCreate ? (
              <>
                <div style={{ fontSize: '13px', marginBottom: '12px' }}>
                  No projections exist for this quarter yet. Creating them will pull in your open job/job-task lines{hideNonJobsAndGoal ? '' : ' and open opportunities where you are the director'}, and default amounts for the overlapping months from your prior budget.
                </div>
                <button className="btn btn-primary" disabled={busy} onClick={doCreate}>
                  {busy ? 'Creating…' : 'Create Projections'}
                </button>
              </>
            ) : (
              <div style={{ fontSize: '13px', color: 'var(--gray-500)' }}>
                No projections exist for this quarter and they cannot be created (past quarter).
              </div>
            )}
          </div>
        </div>
      )}

      {/* Submitted budgets: name, months, window total, actual invoiced, variance (read-only) */}
      {status !== 'none' && readOnly && (
        <>
        {renderGoalPanel(true)}
        <div className="card">
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', padding: '8px 12px', borderBottom: '1px solid var(--gray-100)' }}>
            <button className="btn btn-sm btn-primary" disabled={busy} onClick={doLoadActuals}>
              {busy ? 'Working…' : 'Load Actual Invoiced Amounts'}
            </button>
            <span style={{ fontSize: '12px', color: 'var(--gray-500)' }}>
              Actuals last loaded: {actualModified ? new Date(actualModified).toLocaleString() : 'never'}
            </span>
            <div style={{ flex: 1 }} />
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
              <input type="checkbox" checked={hideZero} onChange={(e) => setHideZero(e.target.checked)} />
              Hide zero lines
            </label>
          </div>
          <div style={scrollWrap}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={stickyHead}>Name / Description</th>
                  {monthLabels.map((lbl, k) => (
                    <th key={k} style={{ ...stickyHead, textAlign: 'right' }}>{lbl}</th>
                  ))}
                  <th style={{ ...stickyHead, textAlign: 'right' }}>6 mos. (Projected)</th>
                  <th style={{ ...stickyHead, textAlign: 'right' }}>Actual Invoiced</th>
                  <th style={{ ...stickyHead, textAlign: 'right' }}>Variance</th>
                </tr>
                <tr>
                  <td style={stickyTotals}>Totals</td>
                  {totals.m.map((mv, k) => (
                    <td key={k} style={{ ...stickyTotals, textAlign: 'right' }}>{fmtMoney(mv)}</td>
                  ))}
                  <td style={{ ...stickyTotals, textAlign: 'right' }}>{fmtMoney(totals.q)}</td>
                  <td style={{ ...stickyTotals, textAlign: 'right' }}>{fmtMoney(totals.actual)}</td>
                  <td style={{ ...stickyTotals, textAlign: 'right', color: totals.variance < 0 ? '#dc2626' : undefined }}>{fmtMoney(totals.variance)}</td>
                </tr>
              </thead>
              <tbody>
                {[['Jobs', jobLines], ['Opportunities', oppLines], ['Other', otherLines]]
                  .filter(([title]) => !hideNonJobsAndGoal || title === 'Jobs')
                  .map(([title, allRows]) => {
                  const rows = hideZero ? allRows.filter(({ l }) => !isZeroLine(l)) : allRows;
                  const isCollapsed = !!collapsed[title];
                  return (
                  <React.Fragment key={title}>
                    <tr><td colSpan={SUB_COLS} style={{ ...sectionRowStyle, cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSection(title)} title={isCollapsed ? 'Expand section' : 'Collapse section'}>{isCollapsed ? '▸' : '▾'} {title}</td></tr>
                    {!isCollapsed && rows.length === 0 && (
                      <tr><td style={cellStyle} colSpan={SUB_COLS}><span style={{ color: 'var(--gray-400)' }}>{hideZero && allRows.length > 0 ? 'None (all zero lines hidden).' : 'None.'}</span></td></tr>
                    )}
                    {!isCollapsed && rows.map(({ l }) => {
                      const variance = (Number(l.actualInvoiced) || 0) - toInt(l.quarterTotal);
                      return (
                        <tr key={l.lineGroupId}>
                          <td style={cellStyle}>{lineName(l)}</td>
                          {monthLabels.map((_, k) => (
                            <td key={k} style={{ ...cellStyle, textAlign: 'right' }}>{fmtMoney(l.amounts[k])}</td>
                          ))}
                          <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 600 }}>{fmtMoney(l.quarterTotal)}</td>
                          <td style={{ ...cellStyle, textAlign: 'right' }}>{l.actualInvoiced != null ? fmtMoney(l.actualInvoiced) : mutedDash}</td>
                          <td style={{ ...cellStyle, textAlign: 'right', color: variance < 0 ? '#dc2626' : undefined }}>{fmtMoney(variance)}</td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        </>
      )}

      {/* Open budgets: full editable grid */}
      {status !== 'none' && !readOnly && (
        <>
        {renderGoalPanel(false)}
        <div className="card">
          <div style={scrollWrap}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={stickyHead}>Name / Description</th>
                  <th style={{ ...stickyHead, textAlign: 'center' }}></th>
                  <th style={{ ...stickyHead, textAlign: 'center' }}></th>
                  <th style={{ ...stickyHead, textAlign: 'center' }}></th>
                  <th style={{ ...stickyHead, textAlign: 'center' }}></th>
                  {monthLabels.map((lbl, k) => (
                    <th key={k} style={{ ...stickyHead, textAlign: 'right' }}>{lbl}</th>
                  ))}
                  <th style={{ ...stickyHead, textAlign: 'right' }}>6 mos. Total</th>
                  <th style={{ ...stickyHead, textAlign: 'center' }}></th>
                  <th style={{ ...stickyHead, textAlign: 'center' }}></th>
                </tr>
                <tr>
                  <td style={stickyTotals} colSpan={5}>Totals</td>
                  {totals.m.map((mv, k) => (
                    <td key={k} style={{ ...stickyTotals, textAlign: 'right' }}>{fmtMoney(mv)}</td>
                  ))}
                  <td style={{ ...stickyTotals, textAlign: 'right' }}>{fmtMoney(totals.q)}</td>
                  <td style={stickyTotals} colSpan={2}></td>
                </tr>
              </thead>
              <tbody>
                {/* Section 1: Jobs */}
                {sectionBand('Jobs', ['', 'Invoiced Last 3 mos.', 'Budget Remaining'])}
                {!collapsed['Jobs'] && jobLines.length === 0 && (
                  <tr><td style={cellStyle} colSpan={OPEN_COLS}><span style={{ color: 'var(--gray-400)' }}>No job lines.</span></td></tr>
                )}
                {!collapsed['Jobs'] && (() => {
                  const out = [];
                  let prevJob = null;
                  jobLines.forEach(({ l, i }) => {
                    if (l.jobNo !== prevJob) {
                      prevJob = l.jobNo;
                      out.push(
                        <tr key={`jobhdr-${l.jobNo}`}>
                          <td colSpan={OPEN_COLS} style={{ ...cellStyle, fontWeight: 700, background: 'var(--gray-50, #f8fafc)' }}>
                            {l.jobNo} — {l.jobName}{l.customerName ? ` (${l.customerName})` : ''}
                          </td>
                        </tr>
                      );
                    }
                    out.push(renderBudgetRow(l, i));
                  });
                  return out;
                })()}

                {/* Section 2: Opportunities (hidden by the "Hide Non-Jobs" setting) */}
                {!hideNonJobsAndGoal && sectionBand('Opportunities', ['Estimated Value', 'Probability', 'Calculated Value'])}
                {!hideNonJobsAndGoal && !collapsed['Opportunities'] && oppLines.length === 0 && (
                  <tr><td style={cellStyle} colSpan={OPEN_COLS}><span style={{ color: 'var(--gray-400)' }}>No opportunity lines.</span></td></tr>
                )}
                {!hideNonJobsAndGoal && !collapsed['Opportunities'] && oppLines.map(({ l, i }) => renderBudgetRow(l, i))}

                {/* Section 3: Other (hidden by the "Hide Non-Jobs" setting) */}
                {!hideNonJobsAndGoal && sectionBand('Other', ['Estimated Value', 'Probability', 'Calculated Value'],
                  <button className="btn btn-sm btn-primary" disabled={busy} onClick={doAddOther} style={{ fontSize: '11px' }}>+ Add Line</button>
                )}
                {!hideNonJobsAndGoal && !collapsed['Other'] && otherLines.length === 0 && (
                  <tr><td style={cellStyle} colSpan={OPEN_COLS}><span style={{ color: 'var(--gray-400)' }}>No other lines. Use “Add Line” to create one.</span></td></tr>
                )}
                {!hideNonJobsAndGoal && !collapsed['Other'] && otherLines.map(({ l, i }) => renderBudgetRow(l, i))}
              </tbody>
            </table>
          </div>
        </div>
        </>
      )}
    </div>
  );
}
